import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Upload, X, ChevronRight, ChevronLeft, CheckCircle,
  FileText, Image, MessageSquare, Info, BookOpen,
} from 'lucide-react';
import './PublishArticle.css';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import PriceTag from '../../components/PriceTag';

const GUIDE_ITEMS = [
  {
    icon: <FileText size={18} />,
    title: 'Format 5W+1H',
    items: [
      'Siapa (Who): nama orang, instansi, lembaga, atau komunitas yang terlibat',
      'Apa (What): peristiwa, kegiatan, atau hasil yang ingin didokumentasikan',
      'Kapan (When): tanggal, bulan, tahun, atau rentang waktu pelaksanaan',
      'Di mana (Where): lokasi fisik, kota, institusi, atau platform digital',
      'Mengapa (Why): latar belakang, tujuan, atau urgensi kegiatan',
      'Bagaimana (How): metode, proses, alur, atau mekanisme pelaksanaan',
    ],
  },
  {
    icon: <Image size={18} />,
    title: 'Foto Pendukung',
    items: [
      'Unggah hingga 5 foto pendukung per artikel',
      'Tipe file yang diterima: JPG, PNG, atau WebP',
      'Ukuran maksimal 15 MB per foto',
      'Resolusi minimal yang disarankan: 800 x 600 piksel',
      'Foto beresolusi tinggi akan ditampilkan lebih baik di halaman artikel',
      'Sertakan keterangan foto agar editor dapat memberikan konteks yang tepat',
    ],
  },
  {
    icon: <MessageSquare size={18} />,
    title: 'Tips Pengisian',
    items: [
      'Tulis fakta, bukan opini. Editor yang akan menyusunnya menjadi artikel',
      'Semakin lengkap informasi yang diberikan, semakin akurat artikel yang dihasilkan',
      'Gunakan kolom Informasi Tambahan untuk data, kutipan, atau tautan referensi',
      'Pilih topik yang paling sesuai agar artikel mudah ditemukan pembaca',
    ],
  },
  {
    icon: <Info size={18} />,
    title: 'Kebijakan Konten',
    items: [
      'Artikel harus berkaitan dengan inovasi, riset, edukasi, dampak sosial, kolaborasi, atau kisah inspiratif',
      'Informasi yang diajukan harus faktual dan dapat diverifikasi',
      'Tidak diperkenankan: konten promosi berbayar, hoaks, SARA, atau materi yang melanggar hukum',
      'Tim editor berhak menyesuaikan judul, struktur, dan bahasa artikel tanpa mengubah fakta',
    ],
  },
];

const STEPS = [
  { n: 1, l: 'Detail dan Fakta' },
  { n: 2, l: 'Topik dan Foto' },
  { n: 'preview', l: 'Konfirmasi' },
];

export default function PublishArticle() {
  const { settings } = useSiteSettings();
  const sn = settings.siteName;
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = panduan, 1/2/preview = form
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    who: '', what: '', when: '', where: '', why: '', how: '',
    additionalContext: '', categories: [], photos: [],
  });

  useEffect(() => {
    API.get('/categories').then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleCategory = (id) => setForm(p => ({
    ...p,
    categories: p.categories.includes(id) ? p.categories.filter(c => c !== id) : [...p.categories, id],
  }));
  const step1OK = form.who && form.what && form.when && form.where && form.why && form.how;
  const step2OK = form.categories.length > 0;

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files);
    if (form.photos.length + files.length > 5) { toast.error('Maksimal 5 foto'); return; }
    setUploadingPhoto(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append('photo', file);
      try {
        const { data } = await API.post('/upload/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setForm(p => ({ ...p, photos: [...p.photos, { url: data.url, caption: '' }] }));
      } catch { toast.error('Gagal mengunggah foto'); }
    }
    setUploadingPhoto(false);
  };

  const removePhoto = i => setForm(p => ({ ...p, photos: p.photos.filter((_, j) => j !== i) }));
  const updateCaption = (i, cap) => setForm(p => ({
    ...p, photos: p.photos.map((ph, j) => j === i ? { ...ph, caption: cap } : ph),
  }));

  const resetForm = () => {
    setDone(false);
    setStep(0);
    setForm({ who: '', what: '', when: '', where: '', why: '', how: '', additionalContext: '', categories: [], photos: [] });
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        who: form.who, what: form.what, when: form.when, where: form.where,
        why: form.why, how: form.how,
        additionalContext: form.additionalContext || '',
        categories: form.categories,
        photos: form.photos,
      };
      await API.post('/articles/request', payload);
      setSaving(false);
      toast.success('Artikel berhasil dikirim! Silakan selesaikan pembayaran.');
      navigate('/artikel-saya');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengajukan artikel');
      setSaving(false);
    }
  };

  /* Sukses */
  if (done) {
    return (
      <DashboardLayout title="Artikel Terkirim" subtitle="">
        <div className="pub-success">
          <div className="pub-success-icon">
            <CheckCircle size={36} color="#059669" />
          </div>
          <h2 className="pub-success-title">Artikel Berhasil Dikirim!</h2>
          <p className="pub-success-desc">
            Artikel Anda telah masuk ke sistem. Silakan selesaikan pembayaran melalui halaman <strong>Artikel Saya</strong> untuk memulai proses penulisan.
          </p>
          <div className="pub-success-actions">
            <button className="btn btn-primary" onClick={() => navigate('/artikel-saya')}>
              Bayar &amp; Lihat Artikel Saya
            </button>
            <button className="btn btn-secondary" onClick={resetForm}>
              Kirim Artikel Lain
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  /* Step indicator (hanya ditampilkan saat form aktif) */
  const StepBar = () => (
    <div className="pub-stepbar">
      {STEPS.map((s, i) => {
        const isActive = step === s.n;
        const isDone =
          (s.n === 1 && (step === 2 || step === 'preview')) ||
          (s.n === 2 && step === 'preview');
        return (
          <React.Fragment key={s.n}>
            <div className="pub-step">
              <div className={`pub-step-circle ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
                {isDone ? '✓' : i + 1}
              </div>
              <span className={`pub-step-label ${isActive || isDone ? 'active' : ''}`}>{s.l}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`pub-step-line ${isDone ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  /* Panduan (step 0) */
  if (step === 0) {
    return (
      <DashboardLayout title="Kirim Artikel" subtitle="Baca panduan berikut sebelum mengisi formulir">
        <div className="pub-wrap">

          <div className="pub-guide-header">
            <div className="pub-guide-header-icon">
              <BookOpen size={22} />
            </div>
            <div>
              <div className="pub-guide-header-title">Panduan Pengajuan Artikel</div>
              <div className="pub-guide-header-sub">
                Anda tidak perlu menulis artikel sendiri. Cukup berikan fakta dan foto,
                tim editor {sn} yang akan menyusunnya menjadi artikel terstruktur.
              </div>
            </div>
          </div>

          <div className="pub-guide-grid">
            {GUIDE_ITEMS.map(g => (
              <div key={g.title} className="pub-guide-card">
                <div className="pub-guide-card-head">
                  <span className="pub-guide-card-icon">{g.icon}</span>
                  <span className="pub-guide-card-title">{g.title}</span>
                </div>
                <ul className="pub-guide-list">
                  {g.items.map(item => <li key={item}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>

          {/* Alur proses */}
          <div className="pub-guide-flow">
            <div className="pub-guide-flow-title">Alur Proses Setelah Pengajuan</div>
            <div className="pub-flow-steps">
              {[
                { n: '01', label: 'Pengajuan diterima', desc: 'Formulir Anda masuk ke antrian editor.' },
                { n: '02', label: 'Penulisan artikel', desc: 'Editor menyusun artikel berdasarkan fakta yang Anda berikan.' },
                { n: '03', label: 'Tinjauan produser', desc: 'Produser memeriksa akurasi dan kualitas editorial.' },
                { n: '04', label: 'Persetujuan Anda', desc: 'Anda membaca draf akhir dan memberikan persetujuan.' },
                { n: '05', label: 'Tayang', desc: 'Artikel diterbitkan dan dapat dibaca publik.' },
              ].map((s, i, arr) => (
                <React.Fragment key={s.n}>
                  <div className="pub-flow-step">
                    <div className="pub-flow-step-n">{s.n}</div>
                    <div className="pub-flow-step-label">{s.label}</div>
                    <div className="pub-flow-step-desc">{s.desc}</div>
                  </div>
                  {i < arr.length - 1 && <div className="pub-flow-arrow">›</div>}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={() => setStep(1)}>
              Mulai Pengajuan <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Kirim Artikel"
      subtitle="Ceritakan fakta utama, tim editorial yang akan menyusunnya"
    >
      <div className="pub-wrap">
        <StepBar />

        {/* Step 1: 5W+1H */}
        {step === 1 && (
          <div className="ki-card">
            <div className="pub-card-title">Detail dan Fakta (5W+1H)</div>
            <div className="alert alert-info" style={{ marginBottom: 20 }}>
              Berikan informasi faktual selengkap mungkin. Tidak perlu menulis artikel, cukup fakta-faktanya.
            </div>
            {[
              { key: 'who', label: 'Siapa (Who) *', placeholder: 'Nama orang, instansi, lembaga, atau komunitas yang terlibat', rows: 2 },
              { key: 'what', label: 'Apa (What) *', placeholder: 'Apa yang terjadi, dilakukan, atau dihasilkan?', rows: 2 },
              { key: 'when', label: 'Kapan (When) *', placeholder: 'Tanggal, bulan, tahun, atau rentang waktu kegiatan', rows: 2 },
              { key: 'where', label: 'Di mana (Where) *', placeholder: 'Lokasi, kota, institusi, atau platform digital', rows: 2 },
              { key: 'why', label: 'Mengapa (Why) *', placeholder: 'Latar belakang, tujuan, atau urgensi kegiatan ini', rows: 3 },
              { key: 'how', label: 'Bagaimana (How) *', placeholder: 'Metode, proses, alur, atau mekanisme pelaksanaannya', rows: 3 },
              { key: 'additionalContext', label: 'Informasi Tambahan (opsional)', placeholder: 'Data pendukung, kutipan, capaian, referensi, atau catatan lain', rows: 3 },
            ].map(f => (
              <div key={f.key} className="form-group">
                <label className="form-label">{f.label}</label>
                <textarea
                  className="form-textarea" rows={f.rows}
                  placeholder={f.placeholder}
                  value={form[f.key]}
                  onChange={e => up(f.key, e.target.value)}
                />
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn btn-ghost" onClick={() => setStep(0)}>
                <ChevronLeft size={16} /> Lihat Panduan
              </button>
              <button className="btn btn-primary" disabled={!step1OK} onClick={() => setStep(2)}>
                Lanjut <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Topik + Foto */}
        {step === 2 && (
          <div className="ki-card">
            <div className="pub-card-title">Topik dan Foto</div>

            <div className="form-group">
              <label className="form-label">Pilih Topik * <span style={{ fontWeight: 400, color: 'var(--in-text-faint)' }}>(boleh lebih dari satu)</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {categories.map(cat => {
                  const active = form.categories.includes(cat._id);
                  return (
                    <button
                      key={cat._id} type="button"
                      onClick={() => toggleCategory(cat._id)}
                      style={{
                        padding: '7px 16px', borderRadius: 100, fontSize: 13, fontWeight: 600,
                        cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
                        background: active ? (cat.color || 'var(--in-blue)') : 'var(--in-bg-3)',
                        color: active ? '#fff' : 'var(--in-text-muted)',
                        border: '1px solid ' + (active ? (cat.color || 'var(--in-blue)') : 'var(--in-border)'),
                      }}
                    >{cat.name}</button>
                  );
                })}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Foto Pendukung (opsional, maks. 5)</label>
              <div className="pub-guide-spec-note" style={{ marginBottom: 10 }}>
                JPG, PNG, atau WebP. Ukuran maksimal 15 MB per foto. Resolusi minimal 800 x 600 piksel.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 12 }}>
                {form.photos.map((ph, i) => (
                  <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', background: 'var(--in-bg-3)' }}>
                    <img src={ph.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button onClick={() => removePhoto(i)} style={{
                      position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}><X size={12} /></button>
                  </div>
                ))}
                {form.photos.length < 5 && (
                  <label style={{
                    aspectRatio: '1', border: '2px dashed var(--in-border)', borderRadius: 8,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', background: 'var(--in-bg-3)', transition: 'all 0.2s',
                  }}>
                    <Upload size={18} style={{ color: 'var(--in-text-faint)' }} />
                    <span style={{ fontSize: 10, color: 'var(--in-text-faint)', marginTop: 4 }}>
                      {uploadingPhoto ? 'Mengunggah...' : '+ Foto'}
                    </span>
                    <input type="file" accept="image/*" multiple hidden onChange={uploadPhoto} disabled={uploadingPhoto} />
                  </label>
                )}
              </div>
              {form.photos.map((ph, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <img src={ph.url} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                  <input
                    type="text" className="form-input"
                    placeholder={`Keterangan foto ${i + 1} (opsional)`}
                    value={ph.caption}
                    onChange={e => updateCaption(i, e.target.value)}
                    style={{ fontSize: 12 }}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>
                <ChevronLeft size={16} /> Kembali
              </button>
              <button className="btn btn-primary" disabled={!step2OK} onClick={() => setStep('preview')}>
                Lanjut ke Konfirmasi <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Konfirmasi */}
        {step === 'preview' && (
          <div className="ki-card">
            <div className="pub-card-title">Konfirmasi Pengiriman</div>
            <div className="pub-preview-box">
              {[
                ['Apa', form.what],
                ['Siapa', form.who],
                ['Kapan', form.when],
                ['Di mana', form.where],
                ['Topik', form.categories.map(id => categories.find(c => c._id === id)?.name).filter(Boolean).join(', ') || '-'],
                ['Foto', form.photos.length > 0 ? `${form.photos.length} foto` : 'Tidak ada'],
              ].map(([l, v]) => (
                <div key={l} className="pub-preview-row">
                  <span className="pub-preview-label">{l}</span>
                  <span className="pub-preview-val">{v}</span>
                </div>
              ))}
            </div>
            {!(settings.paymentMode === 'free' || settings.featureArticleRequestFree) && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 16px', marginBottom: 16, background: 'var(--in-bg-3)', border: '1px solid var(--in-border)', borderRadius: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--in-text-muted)' }}>Biaya Publikasi</span>
                <PriceTag size="lg" />
              </div>
            )}
            <div className="alert alert-info">
              Setelah dikirim, tim editor akan meninjau dan menyusun artikel Anda.
              Anda akan mendapat notifikasi saat ada pembaruan status.
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={() => setStep(2)} disabled={saving}>
                <ChevronLeft size={16} /> Kembali
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving} onClick={submit}>
                {saving ? 'Mengirim...' : 'Kirim Artikel'}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
